import { configureStore } from "@reduxjs/toolkit";
import * as log from "loglevel";
import { combineReducers, ReducersMapObject } from "redux";
import { createMigrate, persistStore, persistReducer } from "redux-persist";
import { createBlacklistFilter } from "redux-persist-transform-filter";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware, { SagaMiddlewareOptions } from "redux-saga";
import { all } from "redux-saga/effects";

import { createActionProcessingMiddleware, ActionProcessingConfigType } from "./actionProcessorMiddleware";
import { LocalStorage } from "./LocalStorage";

import { getReduxPartsBuilder, IReducerConfig } from "./";

const persistedReducer = (reducerConfigs: IReducerConfig[], migrations: any) => {
  const transforms = reducerConfigs.map(e => createBlacklistFilter(e.reducerName, e.transformation && e.transformation.path));
  const blackList = reducerConfigs.map(e => {
    return !e.persistLocally ? e.reducerName : "";
  });

  const reducers: ReducersMapObject = {};
  reducerConfigs.forEach(e => {
    reducers[e.reducerName] = e.reducer;
  });

  return persistReducer(
    {
      key: "app",
      storage,
      version: 0,
      transforms: transforms,
      blacklist: blackList,
      stateReconciler: autoMergeLevel2,
      migrate: createMigrate(migrations, { debug: false })
    },
    combineReducers(reducers)
  );
};

const rootSaga = function* rootSaga(sagas: any[]) {
  yield all(sagas);
};

const config: SagaMiddlewareOptions = {
  onError: (error, errorInfo) => {
    log.error("Uncaught saga error:", errorInfo.sagaStack);
    log.error(error);
  }
};

const sagaMiddleware = createSagaMiddleware(config);

export function loadStorage(getMigrations: () => any, middlewareConfig: { actionProcessing: ActionProcessingConfigType }, preloadedState?: any): LocalStorage {
  const reduxPartsBuilder = getReduxPartsBuilder();

  const actionProcessingMiddleware = createActionProcessingMiddleware(middlewareConfig.actionProcessing);

  const store = configureStore({
    reducer: persistedReducer(reduxPartsBuilder.getReducers(), getMigrations()),
    middleware: [sagaMiddleware, actionProcessingMiddleware],
    devTools: process.env.REACT_APP_REDUX_DEV_TOOLS_ENABLED === "1",
    preloadedState: preloadedState
  });

  const sagas = rootSaga(reduxPartsBuilder.getSagas());

  sagaMiddleware.run(() => sagas);

  return new LocalStorage(store, persistStore(store));
}
